import type { AttackAction } from "../types/actions";
import type { BoardCell, WarchestState } from "../types/game-state";
import {
  advanceActivePlayer,
  allHandsEmpty,
  drawCoinsFromBag,
  moveCoin,
} from "../utils/zone-utils";

export function applyAttack(
  state: WarchestState,
  action: AttackAction,
): WarchestState {
  const zones = moveCoin(
    state.playerZones[action.player],
    action.coinType,
    "hand",
    "discardFaceUp",
  );
  const targetCell = state.board[action.targetHex] as BoardCell;
  const targetOwner = targetCell.owner;

  const targetZonesWithRemoved = {
    ...state.playerZones[targetOwner],
    removed: [...state.playerZones[targetOwner].removed, targetCell.unitType],
  };

  const newTargetStackSize = targetCell.stackSize - 1;
  let newBoard = {
    ...state.board,
    [action.targetHex]:
      newTargetStackSize > 0
        ? { ...targetCell, stackSize: newTargetStackSize }
        : null,
  };

  let newPlayerZones = {
    ...state.playerZones,
    [action.player]: zones,
    [targetOwner]: targetZonesWithRemoved,
  };

  // PIKEMAN_THORNS: attacker also loses a coin when attacking Pikeman
  const targetUnitDef = state.config.units[targetCell.unitType];
  if (targetUnitDef?.attributeId === "PIKEMAN_THORNS") {
    const attackerCell = state.board[action.attackerHex] as BoardCell;
    const newAttackerStackSize = attackerCell.stackSize - 1;
    newBoard = {
      ...newBoard,
      [action.attackerHex]:
        newAttackerStackSize > 0
          ? { ...attackerCell, stackSize: newAttackerStackSize }
          : null,
    };
    if (newAttackerStackSize <= 0) {
      newPlayerZones = {
        ...newPlayerZones,
        [action.player]: {
          ...newPlayerZones[action.player],
          removed: [
            ...newPlayerZones[action.player].removed,
            attackerCell.unitType,
          ],
        },
      };
    }
  }

  // SWORDSMAN_FOLLOW_THROUGH: move Swordsman to follow-up hex after attacking
  if (
    action.swordsmanFollowUpHex !== undefined &&
    state.config.units[action.coinType]?.attributeId ===
      "SWORDSMAN_FOLLOW_THROUGH" &&
    newBoard[action.attackerHex] !== null
  ) {
    const attackerCell = newBoard[action.attackerHex] as BoardCell;
    newBoard = {
      ...newBoard,
      [action.attackerHex]: null,
      [action.swordsmanFollowUpHex]: attackerCell,
    };
  }

  // WARRIOR_PRIEST_DRAW: draw a coin from bag after attacking
  let rngState = state.rngState;
  if (
    state.config.units[action.coinType]?.attributeId === "WARRIOR_PRIEST_DRAW"
  ) {
    const { zones: drawnZones, rngState: newRng } = drawCoinsFromBag(
      newPlayerZones[action.player],
      1,
      rngState,
    );
    newPlayerZones = { ...newPlayerZones, [action.player]: drawnZones };
    rngState = newRng;
  }

  const nextActive = advanceActivePlayer(action.player, newPlayerZones);
  const nextPhase = allHandsEmpty(newPlayerZones)
    ? ("DRAW_COINS" as const)
    : state.phase;
  return {
    ...state,
    board: newBoard,
    playerZones: newPlayerZones,
    activePlayer: nextActive,
    phase: nextPhase,
    rngState,
    initiativeTransferredThisRound:
      nextPhase === "DRAW_COINS" ? false : state.initiativeTransferredThisRound,
    turn: nextPhase === "DRAW_COINS" ? state.turn + 1 : state.turn,
  };
}
